import { useEffect, useId, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Bot, Maximize2, Minus, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { pageContextFor, widgetHiddenOn } from '@/src/lib/assistant/session'
import { AssistantThreadView } from './AssistantThread'
import { useAssistantSession } from './AssistantSession'
import { VOICE_STATUS_LABEL } from './VoicePanel'

/** New conversation and the full page, beside the title. */
function PanelActions({ onNew, onFullPage }: { onNew: () => void; onFullPage: () => void }) {
  return (
    <div className="flex items-center gap-1">
      <Button size="icon" variant="ghost" onClick={onNew} aria-label="New conversation">
        <Plus className="size-4" />
      </Button>
      <Button size="icon" variant="ghost" asChild>
        <Link to="/assistant" onClick={onFullPage} aria-label="Open Beacon full page">
          <Maximize2 className="size-4" />
        </Link>
      </Button>
    </div>
  )
}

/**
 * Beacon on every page: a launcher in the corner that opens the conversation
 * as a floating panel, or as a sheet on a phone. The conversation and any call
 * belong to the session above the router, so the panel can close and the
 * person can move around Helm while Beacon keeps listening; the launcher shows
 * that it is.
 *
 * Steps aside on /assistant, which shows the same conversation full size.
 */
export function AssistantWidget({ userId }: { userId: string }) {
  const session = useAssistantSession()
  const { assistant, panelOpen, openPanel, closePanel, selectThread, bindUser, setPage, activate } =
    session
  const location = useLocation()
  const phone = useIsMobile()
  const titleId = useId()
  const panelRef = useRef<HTMLElement>(null)

  useEffect(() => bindUser(userId), [bindUser, userId])
  useEffect(() => setPage(pageContextFor(location.pathname)), [setPage, location.pathname])
  useEffect(() => {
    if (!panelOpen) return
    activate()
    // Into the panel, so the keyboard and a screen reader follow it there.
    panelRef.current?.focus()
  }, [panelOpen, activate])

  // Only the shell going away (an error page, signing out) unmounts this: with
  // nothing left on screen to end a call from, end it.
  const stopVoice = assistant.voice.stop
  useEffect(() => () => stopVoice(), [stopVoice])

  if (widgetHiddenOn(location.pathname)) return null

  const voice = assistant.voice
  const live = voice.active
  const status = voice.muted && live ? 'Muted' : VOICE_STATUS_LABEL[voice.status]
  const thread = <AssistantThreadView assistant={assistant} mentionItems={session.mentionItems} />

  return (
    <>
      {/* Room at the end of every page, so the launcher never covers its last row. */}
      <div aria-hidden className="h-16" />

      {!panelOpen && (
        <Button
          onClick={openPanel}
          aria-haspopup="dialog"
          aria-label={live ? `Beacon: ${status}` : 'Open Beacon'}
          data-tour="beacon-launcher"
          className={cn(
            'fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-40 h-12 rounded-full shadow-lg',
            live ? 'gap-2 px-4' : 'w-12 px-0 md:w-auto md:px-4',
          )}
        >
          <Bot className="size-5" />
          {live ? (
            <>
              <span
                aria-hidden
                className={cn(
                  'size-2 rounded-full',
                  voice.status === 'speaking' && 'animate-pulse bg-brand',
                  voice.status === 'listening' && (voice.muted ? 'bg-muted' : 'bg-done'),
                  (voice.status === 'connecting' ||
                    voice.status === 'reconnecting' ||
                    voice.status === 'thinking') &&
                    'animate-pulse bg-primary-foreground',
                )}
              />
              <span className="text-sm">{status}</span>
            </>
          ) : (
            <span className="hidden text-sm md:inline">Beacon</span>
          )}
        </Button>
      )}

      {phone ? (
        <Sheet open={panelOpen} onOpenChange={(open) => (open ? openPanel() : closePanel())}>
          <SheetContent
            side="bottom"
            className="h-[88dvh] gap-0 rounded-t-xl p-0 pb-[env(safe-area-inset-bottom)]"
          >
            <header className="flex items-center gap-2 border-b border-border py-2 pr-12 pl-3">
              <Bot className="size-4 text-muted-foreground" />
              <SheetTitle className="text-sm font-medium">Beacon</SheetTitle>
              <SheetDescription className="sr-only">
                Your conversation with Beacon. It keeps going while this is closed.
              </SheetDescription>
              <div className="ml-auto">
                <PanelActions onNew={() => selectThread(null)} onFullPage={closePanel} />
              </div>
            </header>
            <div className="min-h-0 flex-1">{thread}</div>
          </SheetContent>
        </Sheet>
      ) : (
        panelOpen && (
          <section
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="false"
            aria-labelledby={titleId}
            onKeyDown={(event) => {
              // The mention picker claims Escape first when it is open.
              if (event.key === 'Escape' && !event.defaultPrevented) closePanel()
            }}
            className="fixed right-4 bottom-4 z-40 flex h-[min(42rem,calc(100dvh-6rem))] outline-none w-[26rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl"
          >
            <header className="flex items-center gap-2 border-b border-border py-2 pr-2 pl-3">
              <Bot className="size-4 text-muted-foreground" />
              <h2 id={titleId} className="text-sm font-medium">
                Beacon
              </h2>
              <div className="ml-auto flex items-center gap-1">
                <PanelActions onNew={() => selectThread(null)} onFullPage={closePanel} />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={closePanel}
                  aria-label="Minimise Beacon"
                >
                  <Minus className="size-4" />
                </Button>
              </div>
            </header>
            <div className="min-h-0 flex-1">{thread}</div>
          </section>
        )
      )}
    </>
  )
}
